import { CapacityBanner } from './CapacityBanner';
import type { CapacityInfo } from './api';
import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, DEFAULT_TARGET, isRateLimited, type Candidate, type CommitInfo, type GateState, type Maintainer, type Me, type OverlayBaselineRow, type OverlayPinRow, type RateLimitInfo, type ReleaseRequest, type Status, type TargetInfo } from './api';
import { TargetSwitcher } from './TargetSwitcher';
import { PromoteHero } from './PromoteHero';
import { promoStates, redeployableTenants, type RowPromoState } from './promotionState';
import { SKINS, SKIN_ORDER, useSkin, type SkinId } from './skins';
import { LoadingSpinner } from './LoadingSpinner';
import { RateLimitBanner } from './RateLimitBanner';
import { appendPage, EMPTY_LIST, lastPageHasMore, mergeRows, replaceHead, type CandidateList } from './candidatePages';

const sha7 = (s: string) => s.slice(0, 7);
const REFRESH_MS = 10_000;
// Candidate gate badges (tests/staging/e2e) are N GitHub calls to rebuild, so poll
// them gentler than requests and only while the tab is visible (see the effect).
const CANDIDATES_MS = 30_000;

// A rate-limited candidates read must not discard the requests and maintainers loaded beside it.
function candidatesOrLimit(target: string) {
  return api.candidates(target).catch((e: unknown) => {
    if (isRateLimited(e)) return { limited: e.rateLimit };
    throw e;
  });
}

// Fallbacks support API responses issued before target display metadata existed.
const TARGET_REPO: Record<string, string> = {
  bike4mind: 'Bike4Mind/bike4mind',
  polaris: 'FuturumGroup/polaris',
};
const GATE_LABELS: Record<string, { staging: string; tests: string; e2e?: string }> = {
  bike4mind: { staging: 'staging', tests: 'tests', e2e: 'e2e' },
  polaris: { staging: 'CI Gate', tests: 'dev', e2e: 'feq-dev' },
};
function repoFor(targetId: string): string {
  return TARGET_REPO[targetId] ?? TARGET_REPO[DEFAULT_TARGET];
}
function gateLabelsFor(targetId: string) {
  return GATE_LABELS[targetId] ?? GATE_LABELS[DEFAULT_TARGET];
}

// Threads the selected target's repo + gate wording to the deeply-nested bits
// (commit links, gate chips) without prop-drilling it through every list row.
const TargetUiContext = createContext({ repo: TARGET_REPO[DEFAULT_TARGET], gateLabels: GATE_LABELS[DEFAULT_TARGET] });
function useTargetUi() {
  return useContext(TargetUiContext);
}

const STATUS_LABEL: Record<Status, string> = {
  pending: 'AWAITING QUORUM',
  dispatched: 'DISPATCHED',
  rejected: 'REJECTED',
};

/** `?target=` if present and still authorized; else the target saved from a
 * previous visit if still authorized; else DEFAULT_TARGET if authorized; else
 * the first authorized target. */
function resolveTargetId(list: TargetInfo[], requested: string | null, saved: string | null): string {
  if (requested && list.some(t => t.id === requested)) return requested;
  if (saved && list.some(t => t.id === saved)) return saved;
  if (list.some(t => t.id === DEFAULT_TARGET)) return DEFAULT_TARGET;
  return list[0]?.id ?? DEFAULT_TARGET;
}

function readUrlTarget(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('target');
  } catch {
    return null;
  }
}

/** The OAuth callback sends a refused sign-in home as `/?signin=denied`. */
function readSigninDenied(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('signin') === 'denied';
  } catch {
    return false;
  }
}

function clearSigninDenied() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('signin')) return;
    url.searchParams.delete('signin');
    window.history.replaceState(null, '', url);
  } catch {
    /* URL/history unavailable (e.g. some test environments) */
  }
}

function writeUrlTarget(id: string, list: TargetInfo[]) {
  try {
    const url = new URL(window.location.href);
    // Only meaningful once there is something to switch between; keeps a
    // single-target console's URL untouched.
    if (list.length > 1) url.searchParams.set('target', id);
    else url.searchParams.delete('target');
    window.history.replaceState(null, '', url);
  } catch {
    /* URL/history unavailable (e.g. some test environments) */
  }
}

const TARGET_STORAGE_PREFIX = 'deployConsole.target';

// Scoped per login so a shared machine doesn't leak one maintainer's target
// pick to another; falls back to the unscoped key when login isn't known.
function targetStorageKey(login: string | null | undefined): string {
  return login ? `${TARGET_STORAGE_PREFIX}.${login}` : TARGET_STORAGE_PREFIX;
}

function readSavedTarget(login: string | null | undefined): string | null {
  try {
    return localStorage.getItem(targetStorageKey(login));
  } catch {
    return null;
  }
}

function writeSavedTarget(login: string | null | undefined, id: string) {
  try {
    localStorage.setItem(targetStorageKey(login), id);
  } catch {
    /* private mode or blocked storage: falls back to unpersisted, today's behavior */
  }
}

/** Falls back to the bike4mind-only shape a legacy session implies, so a
 * transient /api/targets failure never costs an already-authorized maintainer
 * their promote rights. */
function fallbackTargets(m: Me): TargetInfo[] {
  return m.authorized
    ? [{ id: DEFAULT_TARGET, label: 'Bike4Mind', tenants: [], features: { overlayPins: true, forcePromote: true, redeploy: true }, prodKind: 'statuses' as const }]
    : [];
}

/** True while `ref` still holds the token it held when called and that token is for
 * `id`, so a callback captured before a switch never counts as latest either. */
function latestFor(ref: { current: { id: string } | null }, id: string): () => boolean {
  const token = ref.current;
  return () => token?.id === id && ref.current === token;
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  // Read once, then stripped from the URL so a refresh doesn't bring the notice back.
  const [signinDenied] = useState(readSigninDenied);
  useEffect(() => { clearSigninDenied(); }, []);
  const [targets, setTargets] = useState<TargetInfo[]>([]);
  const [targetId, setTargetId] = useState<string>(DEFAULT_TARGET);
  // The /api/candidates head window plus the older rows Load more appended below it.
  const [candidateList, setCandidateList] = useState<CandidateList>(EMPTY_LIST);
  const [loadingMore, setLoadingMore] = useState(false);
  const [requests, setRequests] = useState<ReleaseRequest[]>([]);
  const [maintainers, setMaintainers] = useState<Maintainer[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ c: Candidate; force: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'promote' | 'history' | 'overlays' | 'howto'>('promote');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  // How far current prod is behind main (from the API); feeds both the sentinel
  // and the in-window caption below the candidate list.
  const [prodAheadBy, setProdAheadBy] = useState<number | undefined>(undefined);
  // Authoritative current-prod SHA from the API: the last promotion that actually
  // deployed green (production/deploy=success), NOT merely the last one dispatched.
  const [apiCurrentProdSha, setApiCurrentProdSha] = useState<string | undefined>(undefined);
  // The newest dispatched promotion + its real deploy state, so a failed/in-flight
  // promote is shown as such instead of masquerading as live.
  const [latestPromotion, setLatestPromotion] = useState<{ sha: string; state: string } | undefined>(undefined);
  // Current prod hydrated directly, for when it has fallen outside the candidates
  // window (the API only sets it then).
  const [prodCandidate, setProdCandidate] = useState<Candidate | undefined>(undefined);
  // Set when the mainline walk hit its cap before reaching current prod: prodAheadBy
  // is then a lower bound (rows actually walked), not the exact distance, so the
  // sentinel says "more than" instead of an exact count.
  const [prodBeyondWindow, setProdBeyondWindow] = useState(false);
  const [prodCommit, setProdCommit] = useState<CommitInfo | null>(null);
  // deployments-kind targets only (polaris): current prod's own deploy time (it
  // can predate the console entirely, so there may be no request to carry one),
  // the feq-production tenant's current SHA (for the redeploy tenant
  // restriction), and the sha to actually select+dispatch to redeploy - a normal
  // candidate that mirrors prod's code but usually isn't prod's own SHA.
  const [prodDeployedAt, setProdDeployedAt] = useState<string | undefined>(undefined);
  const [feqProdSha, setFeqProdSha] = useState<string | undefined>(undefined);
  const [redeployTarget, setRedeployTarget] = useState<(Candidate & { mirrorsProdSha: string }) | undefined>(undefined);
  // The current target's active GitHub rate limit, and the served snapshot's age
  // (null on a hard 503). Both reset on target switch.
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null);
  const [capacity, setCapacity] = useState<CapacityInfo | null>(null);
  const [staleAsOf, setStaleAsOf] = useState<string | null>(null);
  // Read by the candidates poll so backoff never has to re-register the interval.
  const rateLimitResetRef = useRef<number | null>(null);
  useEffect(() => {
    rateLimitResetRef.current = rateLimit ? Date.parse(rateLimit.resetAt) : capacity?.retryAt ? Date.parse(capacity.retryAt) : null;
  }, [rateLimit, capacity]);
  // A fresh token on every target pick, the first included. A load that a newer
  // pick overtakes drops its data, error and loading reset (see latestFor).
  const targetToken = useRef<{ id: string } | null>(null);
  // The target token a Load more page is in flight for; clicks under it are ignored.
  const pageToken = useRef<{ id: string } | null>(null);
  // Displayed rows: the head, then the appended ones. Every row reader uses these.
  const candidates = useMemo(() => mergeRows(candidateList), [candidateList]);

  const pendingRequestReads = useRef(new Map<object, number>());
  const pendingCandidateReads = useRef(new Map<object, number>());
  // A manual refresh may overtake a poll; only the newest read may update the view.
  const requestReadVersion = useRef(0);
  const candidateReadVersion = useRef(0);

  const refreshRequests = useCallback(async (automatic = false) => {
    const token = targetToken.current;
    if (!token || token.id !== targetId) return;
    if (automatic && (loading || document.visibilityState !== 'visible' || pendingRequestReads.current.has(token))) return;
    const version = ++requestReadVersion.current;
    pendingRequestReads.current.set(token, (pendingRequestReads.current.get(token) ?? 0) + 1);
    const isLatest = latestFor(targetToken, targetId);
    try {
      const r = await api.requests(targetId);
      if (isLatest() && version === requestReadVersion.current) setRequests(r);
    } catch (e) {
      if (isLatest() && version === requestReadVersion.current) setError((e as Error).message);
    } finally {
      const remaining = (pendingRequestReads.current.get(token) ?? 1) - 1;
      if (remaining) pendingRequestReads.current.set(token, remaining);
      else pendingRequestReads.current.delete(token);
    }
  }, [targetId, loading]);

  // Re-fetch candidate gate badges (they can flip after load). Only meaningful
  // with a session; stamps lastUpdated so the panel can show freshness.
  const refreshCandidates = useCallback(async (automatic = false) => {
    if (!me?.login) return;
    const token = targetToken.current;
    if (!token || token.id !== targetId) return;
    if (automatic && (loading || document.visibilityState !== 'visible' || pendingCandidateReads.current.has(token))) return;
    const version = ++candidateReadVersion.current;
    pendingCandidateReads.current.set(token, (pendingCandidateReads.current.get(token) ?? 0) + 1);
    const isLatest = latestFor(targetToken, targetId);
    try {
      const r = await api.candidates(targetId);
      if (!isLatest() || version !== candidateReadVersion.current) return;
      setCandidateList(list => replaceHead(list, r.candidates, Boolean(r.mainline)));
      setProdAheadBy(r.prodAheadBy);
      setProdBeyondWindow(Boolean(r.prodBeyondWindow));
      setApiCurrentProdSha(r.currentProdSha);
      setLatestPromotion(r.latestPromotion);
      setProdCandidate(r.prodCandidate);
      setProdDeployedAt(r.prodDeployedAt);
      setFeqProdSha(r.feqProdSha);
      setRedeployTarget(r.redeployTarget);
      setRateLimit(r.rateLimit ?? null);
      setCapacity(r.capacity ?? null);
      setStaleAsOf(r.staleAsOf ?? null);
      setLastUpdated(r.snapshotAsOf ? Date.parse(r.snapshotAsOf) : r.staleAsOf ? Date.parse(r.staleAsOf) : Date.now());
    } catch (e) {
      if (!isLatest() || version !== candidateReadVersion.current) return;
      // A rate limit keeps the rows on screen; the banner stands in for the toast.
      if (isRateLimited(e)) setRateLimit(e.rateLimit);
      else setError((e as Error).message);
    } finally {
      const remaining = (pendingCandidateReads.current.get(token) ?? 1) - 1;
      if (remaining) pendingCandidateReads.current.set(token, remaining);
      else pendingCandidateReads.current.delete(token);
    }
  }, [me?.login, targetId, loading]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refreshCandidates(), refreshRequests()]);
    } finally {
      setRefreshing(false);
    }
  }, [refreshCandidates, refreshRequests]);

  useEffect(() => {
    (async () => {
      let isLatest = () => true;
      try {
        const [m, targetsList] = await Promise.all([
          api.me(),
          api.targets().catch(() => [] as TargetInfo[]),
        ]);
        setMe(m);
        // A session-less caller gets a 401 from /api/targets (caught above as []); an
        // authorized-but-failed fetch shouldn't cost them promote rights they had a
        // moment ago, so it degrades to the bike4mind-only shape instead of empty.
        const resolvedTargets = targetsList.length ? targetsList : fallbackTargets(m);
        setTargets(resolvedTargets);
        const id = resolveTargetId(resolvedTargets, readUrlTarget(), readSavedTarget(m.login));
        setTargetId(id);
        writeUrlTarget(id, resolvedTargets);
        targetToken.current = { id };
        isLatest = latestFor(targetToken, id);
        // The read endpoints require a session; only fetch once we know we have one.
        if (m.login) {
          const [c, r, mn] = await Promise.all([candidatesOrLimit(id), api.requests(id), api.maintainers(id)]);
          if (!isLatest()) return;
          setRequests(r);
          setMaintainers(mn);
          if ('limited' in c) {
            setRateLimit(c.limited);
          } else {
            setCandidateList({ head: c.candidates, older: [], mainline: Boolean(c.mainline) });
            setProdAheadBy(c.prodAheadBy);
            setProdBeyondWindow(Boolean(c.prodBeyondWindow));
            setApiCurrentProdSha(c.currentProdSha);
            setLatestPromotion(c.latestPromotion);
            setProdCandidate(c.prodCandidate);
            setProdDeployedAt(c.prodDeployedAt);
            setFeqProdSha(c.feqProdSha);
            setRedeployTarget(c.redeployTarget);
            setRateLimit(c.rateLimit ?? null);
        setCapacity(c.capacity ?? null);
            setStaleAsOf(c.staleAsOf ?? null);
            setLastUpdated(c.snapshotAsOf ? Date.parse(c.snapshotAsOf) : c.staleAsOf ? Date.parse(c.staleAsOf) : Date.now());
          }
        }
      } catch (e) {
        if (!isLatest()) return;
        if (isRateLimited(e)) setRateLimit(e.rateLimit);
        else setError((e as Error).message);
      } finally {
        if (isLatest()) setLoading(false);
      }
    })();
  }, []);

  // User-driven target switch: update the URL (so a reload lands on the same
  // target), reset the views that would otherwise show stale cross-target data
  // for a beat, then reload everything for the newly selected target.
  const switchTarget = useCallback(async (id: string) => {
    if (id === targetId) return;
    targetToken.current = { id };
    const isLatest = latestFor(targetToken, id);
    setTargetId(id);
    writeUrlTarget(id, targets);
    writeSavedTarget(me?.login ?? null, id);
    setSelected(null);
    setConfirming(null);
    setCandidateList(EMPTY_LIST);
    setLoadingMore(false);
    setRequests([]);
    setMaintainers([]);
    setApiCurrentProdSha(undefined);
    setLatestPromotion(undefined);
    setProdCandidate(undefined);
    setProdBeyondWindow(false);
    setProdCommit(null);
    setProdDeployedAt(undefined);
    setFeqProdSha(undefined);
    setRedeployTarget(undefined);
    setLastUpdated(null);
    setRateLimit(null);
    setCapacity(null);
    setStaleAsOf(null);
    if (!me?.login) return;
    setLoading(true);
    try {
      const [c, r, mn] = await Promise.all([candidatesOrLimit(id), api.requests(id), api.maintainers(id)]);
      if (!isLatest()) return;
      setRequests(r);
      setMaintainers(mn);
      if ('limited' in c) {
        setRateLimit(c.limited);
      } else {
        setCandidateList({ head: c.candidates, older: [], mainline: Boolean(c.mainline) });
        setProdAheadBy(c.prodAheadBy);
        setProdBeyondWindow(Boolean(c.prodBeyondWindow));
        setApiCurrentProdSha(c.currentProdSha);
        setLatestPromotion(c.latestPromotion);
        setProdCandidate(c.prodCandidate);
        setProdDeployedAt(c.prodDeployedAt);
        setFeqProdSha(c.feqProdSha);
        setRedeployTarget(c.redeployTarget);
        setRateLimit(c.rateLimit ?? null);
        setCapacity(c.capacity ?? null);
        setStaleAsOf(c.staleAsOf ?? null);
        setLastUpdated(c.snapshotAsOf ? Date.parse(c.snapshotAsOf) : c.staleAsOf ? Date.parse(c.staleAsOf) : Date.now());
      }
    } catch (e) {
      if (!isLatest()) return;
      if (isRateLimited(e)) setRateLimit(e.rateLimit);
      else setError((e as Error).message);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [targetId, targets, me?.login]);

  useEffect(() => {
    if (!me?.login) return;
    const tick = () => { void refreshRequests(true); };
    const t = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [refreshRequests, me?.login]);

  // Poll the candidate badges too - staging/tests/e2e can flip after load. Gentler
  // than requests, and only while the tab is visible: listing candidates is N GitHub
  // calls, so backgrounded tabs must not burn the shared app-token rate limit. Also
  // refetch immediately when the tab regains focus so a returning user sees current state.
  // Both pause until a rate limit's reset; the first tick after it fetches again.
  useEffect(() => {
    if (!me?.login) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (rateLimitResetRef.current && Date.now() < rateLimitResetRef.current) return;
      void refreshCandidates(true);
    };
    const t = setInterval(tick, CANDIDATES_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [refreshCandidates, me?.login]);

  // Current prod = the SHA the API says actually deployed green (production/deploy=
  // success), resolved back to its promotion request for the "promoted by" metadata.
  // A dispatched-but-failed/in-flight promote is deliberately NOT shown as live - the
  // card falls back to the last SHA that truly shipped. Falls back to null when the
  // API can't confirm any green prod deploy (banner then shows no live pin).
  const currentProd = useMemo(() => {
    if (!apiCurrentProdSha) return null;
    return (
      requests.find(r => r.sha === apiCurrentProdSha) ??
      ({ sha: apiCurrentProdSha } as ReleaseRequest)
    );
  }, [requests, apiCurrentProdSha]);

  // Newest-first for both the capped promote-view list (latest 5) and the full
  // history page, so ordering is deterministic regardless of API order.
  const requestsSorted = useMemo(
    () => [...requests].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
    [requests],
  );

  // Active design skin, mirrored onto <html data-skin> app-wide (default fantasy).
  const { skin, setSkin } = useSkin();

  // The release request behind an in-flight/failed promote, so the hero can show
  // who promoted it + link its run while it is still on the road.
  const promotionReq = useMemo(
    () => (latestPromotion ? requests.find(r => r.sha === latestPromotion.sha) ?? null : null),
    [requests, latestPromotion],
  );

  // /api/targets already only lists enabled targets the session may promote, so
  // membership in it (for whichever target is selected) IS the promote right -
  // this also covers the single-target/legacy-session case, where it collapses
  // to exactly `me.authorized`.
  const currentTarget = targets.find(t => t.id === targetId);
  const canPromote = targets.some(t => t.id === targetId);
  // premium-overlay-maintainers (or a deployer-maintainer, who has it too) can
  // manage the overlay baseline WITHOUT gaining prod-promote rights - but only
  // for a target that has the overlayPins feature (the API 404s the routes
  // otherwise). Absent target info (e.g. no session yet) defaults open so the
  // single-target case is unaffected.
  const canManageOverlays = Boolean(me?.overlayAuthorized) && (currentTarget?.features.overlayPins ?? true);
  const showForcePromote = currentTarget?.features.forcePromote ?? true;
  // Falls back to prodCandidate or redeployTarget when the selection is current
  // prod but fell outside the fetched window - bike4mind hydrates the former,
  // a deployments-kind target (polaris) hydrates the latter instead (see cutRow
  // below / handlers.ts).
  const selectedCandidate = candidates.find(c => c.sha === selected) ??
    (prodCandidate && selected === prodCandidate.sha ? prodCandidate : null) ??
    (redeployTarget && selected === redeployTarget.sha ? redeployTarget : null);
  const selectedGreen = Boolean(selectedCandidate && selectedCandidate.staging && selectedCandidate.tests && selectedCandidate.e2e);

  const currentProdSha = currentProd?.sha ?? null;
  const isDeploymentsProd = currentTarget?.prodKind === 'deployments';
  // deployments-kind targets (polaris): current prod is read straight from
  // Deployments (see currentProdState server-side), which only ever reports a
  // sha whose deploy status is already 'success' - so unlike bike4mind, this
  // does NOT need to also check that the latest promotion's sha matches it. It
  // must not: a push-to-prod outside the console deploys just as validly and
  // would never match a console-tracked request at all. The one thing still
  // worth blocking on is a console request that dispatched but hasn't resolved
  // yet (mirrors rowPromoStates' own 'deploying' criterion below).
  const canRedeploy = isDeploymentsProd
    ? Boolean(currentProdSha && !(latestPromotion && latestPromotion.state === 'pending'))
    // A same-SHA redeploy repaints that commit's prod status, and the current-prod
    // scan then falls back to an older sha until it lands green. Require that first.
    : Boolean(latestPromotion && currentProdSha && latestPromotion.sha === currentProdSha && latestPromotion.state === 'success');
  const redeployAllowed = currentTarget?.features.redeploy ?? true;
  const prodPins = requestsSorted.find(r => r.status === 'dispatched' && r.sha === currentProdSha)?.overlayPins;
  // deployments-kind targets only (polaris): the candidate that mirrors current
  // prod's code, tagged so the promote box/dialog can say what it re-ships. Off
  // from isCurrentProd only while prod itself is off-main (its usual state pre-
  // first-redeploy) - once a console redeploy lands, prod IS this sha, so the
  // two overlap and the UI (PromoteBox, ConfirmModal) has to handle a row that
  // is both at once, not treat them as mutually exclusive.
  const isRedeployTarget = (sha: string) => Boolean(redeployTarget && sha === redeployTarget.sha);
  // The sha that actually divides roll-forward from rollback: current prod's own
  // sha normally, but its redeploy target (the tree-identical main commit) when
  // prod itself is off-main and would never appear in `candidates` at all - see
  // findRedeployTarget server-side. Equal to currentProdSha once prod is on main
  // (post-first-redeploy), so this changes nothing for bike4mind (redeployTarget
  // is never set there) or for polaris once it stops needing a mirror.
  const prodCutSha = redeployTarget?.sha ?? currentProdSha;
  // A candidate is a rollback when it is OLDER than the prod cut. Candidates are
  // newest-first, so "older" == a higher index. Only decidable when both the
  // candidate and the cut SHA are inside the fetched window.
  const isRollback = useCallback(
    (sha: string) => {
      if (!prodCutSha || sha === prodCutSha) return false;
      const cur = candidates.findIndex(c => c.sha === prodCutSha);
      const sel = candidates.findIndex(c => c.sha === sha);
      return cur >= 0 && sel >= 0 && sel > cur;
    },
    [candidates, prodCutSha],
  );

  // Which rows already have a promote behind them: the one actually shipping and
  // any whose request is still collecting approvals. The hero says a promote is
  // in flight but never which commit, so without this the same SHA is easy to
  // promote twice (the server does not reject duplicate requests).
  const rowPromoStates = useMemo(
    () => promoStates({ latestPromotion, currentProdSha, requests }),
    [latestPromotion, currentProdSha, requests],
  );

  // Where the prod cut sits in the fetched list, so the "prod cuts here" divider
  // can be drawn right after that row (rows above = roll-forward, below =
  // rollback). Unset when prod is known but the mainline walk never reached it
  // (prodBeyondWindow) or it otherwise fell outside the window: the sentinel
  // renders instead, standing in on the hydrated cutRow when there is one.
  const prodCutIndex = prodCutSha ? candidates.findIndex(c => c.sha === prodCutSha) : -1;
  const prodInWindow = prodCutIndex >= 0;
  // The stand-in row to hydrate+render when the cut sha falls outside the fetched
  // window: bike4mind hydrates its own current-prod sha as prodCandidate; a
  // deployments-kind target (polaris) hydrates the redeploy target the same way
  // instead (see handlers.ts) - it's never both, so whichever matches wins.
  const cutRow =
    redeployTarget && redeployTarget.sha === prodCutSha ? redeployTarget :
    prodCandidate && prodCandidate.sha === prodCutSha ? prodCandidate :
    null;
  // False once the last page ran out short of the cut; unset again when the cut moves.
  const hasMore = lastPageHasMore(candidateList, prodCutSha ?? undefined);
  // prodBeyondWindow makes prodAheadBy a lower bound (rows walked), and Load more
  // walks further. Otherwise it is the exact distance and stays as the API said.
  const sentinelAheadBy = prodBeyondWindow ? Math.max(prodAheadBy ?? 0, candidates.length) : prodAheadBy;
  // Reached through Load more, prodAheadBy is still the head's lower bound; the
  // cut's index in the merged list is the real distance.
  const captionAheadBy = prodCutIndex >= candidateList.head.length ? prodCutIndex : prodAheadBy;

  // The page below the last row shown, toward the prod cut. Polls and refreshes
  // only ever replace the head (see replaceHead), never refetch these pages.
  async function loadMore() {
    const token = targetToken.current;
    const cursor = candidates[candidates.length - 1]?.sha;
    if (!token || !cursor || pageToken.current === token) return;
    const isLatest = latestFor(targetToken, targetId);
    const query = { after: cursor, offset: candidates.length, stopAt: prodCutSha ?? undefined, mainline: candidateList.mainline };
    pageToken.current = token;
    setLoadingMore(true);
    try {
      const page = await api.candidatesPage(targetId, query);
      if (isLatest()) setCandidateList(list => appendPage(list, query, page));
    } catch (e) {
      if (!isLatest()) return;
      // Handled like a rate-limited poll: the banner instead of a toast, rows kept, no retry.
      if (isRateLimited(e)) setRateLimit(e.rateLimit);
      else setError((e as Error).message);
    } finally {
      if (pageToken.current === token) pageToken.current = null;
      if (isLatest()) setLoadingMore(false);
    }
  }

  function candidateRows() {
    const rows: React.ReactNode[] = [];
    candidates.forEach((c, i) => {
      rows.push(
        <CandidateRow
          key={c.sha}
          c={c}
          selectable={canPromote}
          selected={selected === c.sha}
          rollback={isRollback(c.sha)}
          isCurrentProd={c.sha === currentProdSha}
          isRedeployTarget={isRedeployTarget(c.sha)}
          promoState={rowPromoStates.get(c.sha)}
          onSelect={() => setSelected(c.sha)}
        />,
      );
      if (prodInWindow && i === prodCutIndex) rows.push(<ProdCutDivider key="prod-cut" />);
    });
    if (currentProdSha && !prodInWindow) {
      rows.push(<LoadMoreRow key="load-more" loading={loadingMore} exhausted={hasMore === false} onLoad={loadMore} />);
      rows.push(
        <ProdSentinel
          key="prod-sentinel"
          sha={currentProdSha}
          cutSha={prodCutSha ?? currentProdSha}
          count={candidates.length}
          aheadBy={sentinelAheadBy}
          beyondWindow={prodBeyondWindow}
          prodRowBelow={Boolean(cutRow)}
        />,
      );
      if (cutRow) {
        rows.push(
          <CandidateRow
            key={cutRow.sha}
            c={cutRow}
            selectable={canPromote}
            selected={selected === cutRow.sha}
            rollback={false}
            isCurrentProd={cutRow.sha === currentProdSha}
            isRedeployTarget={isRedeployTarget(cutRow.sha)}
            promoState={rowPromoStates.get(cutRow.sha)}
            onSelect={() => setSelected(cutRow.sha)}
          />,
        );
        rows.push(<ProdCutDivider key="prod-cut" />);
      }
    }
    return rows;
  }

  // Enrich the current-prod card with the deployed commit's metadata. Keyed on the
  // SHA so it refetches only when prod actually changes, not on every 10s poll. The
  // `cancelled` guard drops a stale response if the SHA changes mid-flight.
  useEffect(() => {
    if (!me?.login || !currentProdSha) { setProdCommit(null); return; }
    let cancelled = false;
    api.commit(currentProdSha, targetId)
      .then(c => { if (!cancelled) setProdCommit(c); })
      .catch(() => { if (!cancelled) setProdCommit(null); });
    return () => { cancelled = true; };
  }, [currentProdSha, me?.login, targetId]);

  async function doPromote(overlayRefs?: Record<string, string>, tenants?: string[]) {
    if (!confirming) return;
    setBusy(true);
    setError(null);
    try {
      await api.promote(confirming.c.sha, confirming.force, overlayRefs, targetId, tenants);
      setConfirming(null);
      setSelected(null);
      await refreshAll();
    } catch (e) {
      // Toast only: the server message says nothing was dispatched; the banner follows the candidates read.
      if (isRateLimited(e)) void refreshCandidates();
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function doApprove(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.approve(id, targetId);
      await refreshRequests();
    } catch (e) {
      if (isRateLimited(e)) void refreshCandidates();
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <TargetUiContext.Provider value={{ repo: currentTarget?.repo ?? repoFor(targetId), gateLabels: currentTarget?.gateLabels ?? gateLabelsFor(targetId) }}>
    <div className="app">
      <div className="topline" aria-hidden />
      <div className="scanlines" aria-hidden />
      <Sidebar
        me={me}
        tab={tab}
        onNav={setTab}
        skin={skin}
        setSkin={setSkin}
        canManageOverlays={canManageOverlays}
        targets={targets}
        targetId={targetId}
        onSwitchTarget={switchTarget}
      />

      <div className="content">
        {tab === 'howto' ? (
          <HowTo maintainers={maintainers} me={me} targetId={targetId} />
        ) : tab === 'history' ? (
          <HistoryView requests={requestsSorted} canPromote={canPromote} overlaysAllowed={currentTarget?.features.overlayPins ?? true} onBack={() => setTab('promote')} />
        ) : tab === 'overlays' ? (
          <OverlaysView canManageOverlays={canManageOverlays} targetId={targetId} onBack={() => setTab('promote')} />
        ) : (
          <main data-testid="promote-view">
            {signinDenied && !loading && !me?.login && <SigninDeniedNotice />}
            <PromoteHero
              product={currentTarget}
              currentProd={currentProd}
              prodCommit={prodCommit}
              promotion={latestPromotion}
              promotionReq={promotionReq}
              skin={skin}
              repo={currentTarget?.repo ?? repoFor(targetId)}
              prodDeployedAt={prodDeployedAt}
            />

            <div className="grid">
            <section className="col">
              {capacity && <CapacityBanner capacity={capacity} staleAsOf={staleAsOf} />}
              {rateLimit && (
                <RateLimitBanner
                  targetLabel={currentTarget?.label ?? targetId}
                  rateLimit={rateLimit}
                  staleAsOf={staleAsOf}
                />
              )}
              <Panel
                title="Promotion candidates"
                hint={lastUpdated ? `updated ${new Date(lastUpdated).toLocaleTimeString()}` : 'latest main commits'}
                action={me?.login ? <RefreshButton busy={refreshing} onClick={refreshAll} /> : undefined}
              >
                {loading ? (
                  <LoadingSpinner label="Loading commits" />
                ) : !me?.login ? (
                  <Empty>Sign in to view promotion candidates.</Empty>
                ) : candidates.length === 0 ? (
                  <Empty>{rateLimit ? 'Commits unavailable while GitHub is rate limited.' : 'No commits found.'}</Empty>
                ) : (
                  <>
                    {/* Only the mainline walk makes the count exact; the REST/compare fallback does not. */}
                    {candidateList.mainline && prodInWindow && typeof captionAheadBy === 'number' && captionAheadBy > 0 && (
                      <p className="note">Current prod is {captionAheadBy} commits behind main.</p>
                    )}
                    <ul className="candidates candidate-frame">{candidateRows()}</ul>
                  </>
                )}
                {/* A refresh keeps the rows on screen, so its wheel floats over them instead. */}
                {refreshing && !loading && candidates.length > 0 && <LoadingSpinner label="Loading commits" overlay />}
              </Panel>
            </section>

            <section className="col">
              <PromoteBox
                me={me}
                canPromote={canPromote}
                showForcePromote={showForcePromote}
                redeployAllowed={redeployAllowed}
                canRedeploy={canRedeploy}
                overlayPinsEnabled={currentTarget?.features.overlayPins ?? true}
                selected={selectedCandidate}
                selectedGreen={selectedGreen}
                rollback={selectedCandidate ? isRollback(selectedCandidate.sha) : false}
                isCurrentProd={Boolean(selectedCandidate && currentProdSha && selectedCandidate.sha === currentProdSha)}
                isRedeployTarget={Boolean(selectedCandidate && isRedeployTarget(selectedCandidate.sha))}
                mirrorsProdSha={redeployTarget?.mirrorsProdSha}
                promoState={selectedCandidate ? rowPromoStates.get(selectedCandidate.sha) : undefined}
                busy={busy}
                onPromote={() => selectedCandidate && setConfirming({ c: selectedCandidate, force: false })}
                onForce={() => selectedCandidate && setConfirming({ c: selectedCandidate, force: true })}
              />

              <Panel title="Release requests" hint="latest 5 . polls every 10s">
                <button className="history-link history-link-top" onClick={() => setTab('history')} data-testid="release-history-link">
                  Release Request History &rarr;
                </button>
                {requestsSorted.length === 0 ? (
                  <Empty>No promotions yet.</Empty>
                ) : (
                  <ul className="requests">
                    {requestsSorted.slice(0, 5).map(r => (
                      <RequestCard
                        key={r.id}
                        r={r}
                        canApprove={canPromote}
                        overlaysAllowed={currentTarget?.features.overlayPins ?? true}
                        busy={busy}
                        onApprove={() => doApprove(r.id)}
                      />
                    ))}
                  </ul>
                )}
              </Panel>
            </section>
            </div>
          </main>
        )}
      </div>

      {error && (
        <div className="toast" role="alert" data-testid="error-toast">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="dismiss">
            x
          </button>
        </div>
      )}

      {confirming && (
        <ConfirmModal
          c={confirming.c}
          force={confirming.force}
          rollback={isRollback(confirming.c.sha)}
          redeploy={confirming.c.sha === currentProdSha || isRedeployTarget(confirming.c.sha)}
          mirrorsProdSha={isRedeployTarget(confirming.c.sha) ? redeployTarget?.mirrorsProdSha : undefined}
          prodPins={prodPins}
          busy={busy}
          targetId={targetId}
          tenants={
            isRedeployTarget(confirming.c.sha)
              ? redeployableTenants(currentTarget?.tenants ?? [], apiCurrentProdSha, feqProdSha)
              : currentTarget?.tenants
          }
          overlaysAllowed={currentTarget?.features.overlayPins ?? true}
          onCancel={() => setConfirming(null)}
          onConfirm={doPromote}
        />
      )}
    </div>
    </TargetUiContext.Provider>
  );
}

type Tab = 'promote' | 'history' | 'overlays' | 'howto';

// Persistent left sidenav (nexus shell): brand, grouped nav, and the user footer
// (identity + sign-out + theme toggle). Replaces the old top header + tab bar.
// Theme picker: swaps the whole console skin (colours + PromoteHero scene).
function SkinPicker({ skin, setSkin }: { skin: SkinId; setSkin: (s: SkinId) => void }) {
  return (
    <label className="side-theme" title="Console theme">
      <span className="side-theme-label">Theme</span>
      <select
        className="side-theme-select"
        value={skin}
        onChange={e => setSkin(e.target.value as SkinId)}
        data-testid="skin-picker"
        aria-label="Console theme"
      >
        {SKIN_ORDER.map(id => <option key={id} value={id}>{SKINS[id].label}</option>)}
      </select>
    </label>
  );
}

function Sidebar({ me, tab, onNav, skin, setSkin, canManageOverlays, targets, targetId, onSwitchTarget }: {
  me: Me | null;
  tab: Tab;
  onNav: (t: Tab) => void;
  skin: SkinId;
  setSkin: (s: SkinId) => void;
  canManageOverlays: boolean;
  targets: TargetInfo[];
  targetId: string;
  onSwitchTarget: (id: string) => void;
}) {
  const initials = me?.login ? me.login.slice(0, 2).toUpperCase() : '..';
  const canPromote = targets.some(t => t.id === targetId);
  return (
    <aside className="sidebar">
      <div>
        <div className="side-brand">
          <img className="brand-mark" src="/console-logo.svg" alt="" height="40" aria-hidden />
          <span className="side-name">DEPLOY<br /><b>CONSOLE</b></span>
        </div>
        <p className="side-sub">prod promotion console</p>
        <TargetSwitcher key={me?.login ?? 'anonymous'} targets={targets} targetId={targetId} onSwitch={onSwitchTarget} recentStorageKey={me?.login ? `deploy-console:recent-targets:${me.login}` : undefined} />
      </div>

      <nav className="side-nav" role="tablist">
        <div className="side-group">
          <h4>Console</h4>
          <button className={`side-item ${tab === 'promote' ? 'active' : ''}`} onClick={() => onNav('promote')} role="tab" aria-selected={tab === 'promote'} data-testid="nav-promote">
            <span className="side-ic" aria-hidden>&#9650;</span> Promote
          </button>
          <button className={`side-item ${tab === 'history' ? 'active' : ''}`} onClick={() => onNav('history')} role="tab" aria-selected={tab === 'history'} data-testid="nav-history">
            <span className="side-ic" aria-hidden>&#9636;</span> Release request history
          </button>
          {/* Overlay-maintainer-only, same as the roster confidentiality on /api/overlay-baseline. */}
          {canManageOverlays && (
            <button className={`side-item ${tab === 'overlays' ? 'active' : ''}`} onClick={() => onNav('overlays')} role="tab" aria-selected={tab === 'overlays'} data-testid="nav-overlays">
              <span className="side-ic" aria-hidden>&#9875;</span> Overlay baseline
            </button>
          )}
        </div>
        <div className="side-group">
          <h4>Meta</h4>
          <button className={`side-item ${tab === 'howto' ? 'active' : ''}`} onClick={() => onNav('howto')} role="tab" aria-selected={tab === 'howto'} data-testid="nav-howto">
            <span className="side-ic" aria-hidden>&#10022;</span> How it works
          </button>
        </div>
      </nav>

      <div className="side-spacer" />

      <div className="side-user">
        <SkinPicker skin={skin} setSkin={setSkin} />
        {me?.login ? (
          <>
            <div className="side-user-row">
              <span className="side-avatar" aria-hidden>{initials}</span>
              <div className="side-user-id">
                <span className="side-login">@{me.login}</span>
                <span className={`badge ${canPromote ? 'ok' : 'muted'}`}>{canPromote ? 'MAINTAINER' : 'READ-ONLY'}</span>
              </div>
            </div>
            <div className="side-user-actions">
              <form method="post" action="/auth/logout">
                <button className="btn btn-ghost" type="submit">Sign out</button>
              </form>
              <ThemeToggle />
            </div>
          </>
        ) : (
          <div className="side-user-actions">
            <a className="btn btn-ghost" href="/auth/login">Sign in</a>
            <ThemeToggle />
          </div>
        )}
      </div>
    </aside>
  );
}

// Exclusive prod release log: the full request history (the promote view only
// shows the latest 5). Reuses the already-fetched requests; newest first.
const HISTORY_PAGE_SIZE = 15;
type StatusFilter = 'all' | Status;
const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Awaiting quorum' },
  { key: 'dispatched', label: 'Dispatched' },
  { key: 'rejected', label: 'Rejected' },
];

// For each request, the overlay repos whose pin differs from the release that was
// live before it (the latest older dispatched request). This is what makes "since
// which release was X pinned" readable at a glance. The oldest dispatched request
// has nothing to compare against, so it marks nothing rather than everything.
// `requests` is newest-first, so walk it in reverse.
function overlayChangesByRequest(requests: ReleaseRequest[]): Map<string, Set<string>> {
  const changes = new Map<string, Set<string>>();
  let livePins: Record<string, string> | null = null;
  for (let i = requests.length - 1; i >= 0; i--) {
    const request = requests[i];
    const pins = request.overlayPins;
    if (!pins) continue;
    if (livePins) {
      const previous = livePins;
      changes.set(request.id, new Set(Object.keys(pins).filter(repo => previous[repo] !== pins[repo])));
    }
    if (request.status === 'dispatched') livePins = pins;
  }
  return changes;
}

// Search box match: sha or author, plus each overlay's repo / ref / pinned sha
// when the viewer may see overlays (so "v2.77.3" finds the releases that pinned it).
function requestMatchesQuery(r: ReleaseRequest, q: string, includeOverlays: boolean): boolean {
  if (r.sha.toLowerCase().includes(q) || r.requester.toLowerCase().includes(q)) return true;
  if (!includeOverlays) return false;
  return Object.entries(r.overlayPins ?? {}).some(([repo, sha]) =>
    [repo, sha, r.overlayRefs?.[repo] ?? 'main'].some(field => field.toLowerCase().includes(q)));
}

function HistoryView({ requests, canPromote, overlaysAllowed, onBack }: { requests: ReleaseRequest[]; canPromote: boolean; overlaysAllowed: boolean; onBack: () => void }) {
  // Maintainer-only, and only for a target with the overlay-pins feature: hides the
  // private overlay roster from the read-only view and from a target like polaris
  // that has no overlays at all (see RequestCard note).
  const showOverlays = canPromote && overlaysAllowed;
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [forcedOnly, setForcedOnly] = useState(false);
  const [page, setPage] = useState(0);

  // Filter client-side (all requests are already loaded): match sha or author on
  // the search box, plus the status + forced toggles. Resetting page to 0 on any
  // filter change keeps the view from stranding on an empty page.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requests.filter(r => {
      if (status !== 'all' && r.status !== status) return false;
      if (forcedOnly && !r.forced) return false;
      if (q && !requestMatchesQuery(r, q, showOverlays)) return false;
      return true;
    });
  }, [requests, query, status, forcedOnly, showOverlays]);
  const overlayChanges = useMemo(() => (showOverlays ? overlayChangesByRequest(requests) : new Map<string, Set<string>>()), [requests, showOverlays]);

  // Clamp the page for display; state may lag if `requests` shrinks under a poll.
  const pageCount = Math.max(1, Math.ceil(filtered.length / HISTORY_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * HISTORY_PAGE_SIZE;
  const pageRows = filtered.slice(start, start + HISTORY_PAGE_SIZE);
  const resetPage = () => setPage(0);

  return (
    <main className="history-view" data-testid="history-view">
      <button className="history-back" onClick={onBack} data-testid="history-back">&larr; back to promote</button>
      <h1 className="history-title">Release Request History</h1>
      <p className="history-sub">Every production promotion request, newest first.</p>

      <div className="history-controls">
        <input
          className="history-search"
          type="search"
          placeholder={showOverlays ? 'Search commit, author or overlay…' : 'Search commit or author…'}
          value={query}
          onChange={e => { setQuery(e.target.value); resetPage(); }}
          data-testid="history-search"
          aria-label={showOverlays ? 'Search release requests by commit, author or overlay' : 'Search release requests by commit or author'}
        />
        <div className="history-filters" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`history-filter ${status === f.key ? 'is-active' : ''}`}
              aria-pressed={status === f.key}
              onClick={() => { setStatus(f.key); resetPage(); }}
            >
              {f.label}
            </button>
          ))}
          <label className="history-forced-toggle">
            <input type="checkbox" checked={forcedOnly} onChange={e => { setForcedOnly(e.target.checked); resetPage(); }} />
            forced
          </label>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Empty>{requests.length === 0 ? 'No promotions yet.' : 'No matching requests.'}</Empty>
      ) : (
        <>
          <div className="history-table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Commit</th>
                  <th>Requested by</th>
                  <th>Approvals</th>
                  <th>Requested</th>
                  <th>Status</th>
                  {showOverlays && <th>Overlays</th>}
                  <th>Deploy</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => (
                  <tr key={r.id} data-testid={`history-row-${r.id}`}>
                    <td><CommitLink sha={r.sha} /></td>
                    <td className="h-who">@{r.requester}</td>
                    <td className="h-who">{r.approvers.length} / {r.required}</td>
                    <td className="h-when">{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</td>
                    <td>
                      <span className={`badge status-${r.status}`}>{STATUS_LABEL[r.status]}</span>
                      {r.forced && <span className="badge badge-forced">FORCED</span>}
                    </td>
                    {showOverlays && (
                      <td>
                        <HistoryOverlays request={r} changed={overlayChanges.get(r.id)} />
                      </td>
                    )}
                    <td>{r.runUrl ? <RunLink href={r.runUrl} /> : <span className="h-dash">&mdash;</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="history-pager" data-testid="history-pager">
            <span className="history-count">
              {start + 1}&ndash;{Math.min(start + HISTORY_PAGE_SIZE, filtered.length)} of {filtered.length}
              {filtered.length !== requests.length && <span className="history-count-total"> (of {requests.length})</span>}
            </span>
            <div className="history-pager-btns">
              <button disabled={safePage === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="history-prev">&larr; Prev</button>
              <span className="history-page-ind">Page {safePage + 1} / {pageCount}</span>
              <button disabled={safePage >= pageCount - 1} onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} data-testid="history-next">Next &rarr;</button>
            </div>
          </div>
        </>
      )}
    </main>
  );
}

// One history row's frozen overlay set: repo, the ref it resolved from, and the
// pinned commit. Repos whose pin moved since the previously live release are
// flagged so a version bump is easy to locate when scanning down the table.
function HistoryOverlays({ request, changed }: { request: ReleaseRequest; changed: Set<string> | undefined }) {
  const pins = Object.entries(request.overlayPins ?? {}).sort(([a], [b]) => a.localeCompare(b));
  if (pins.length === 0) return <span className="h-dash">&mdash;</span>;
  return (
    <ul className="history-overlay-list" data-testid={`history-overlays-${request.id}`}>
      {pins.map(([repo, sha]) => {
        const isChanged = changed?.has(repo) ?? false;
        return (
          <li key={repo} className={isChanged ? 'is-changed' : undefined} data-changed={isChanged || undefined}>
            <span className="overlay-repo">{repo}</span>
            <span className="history-overlay-ref">{request.overlayRefs?.[repo] ?? 'main'}</span>
            <a href={`https://github.com/Bike4Mind/${repo}/commit/${sha}`} target="_blank" rel="noreferrer">
              <code>{sha.slice(0, 7)}</code>
            </a>
            {isChanged && <span className="history-overlay-changed" title="Changed since the previous release">changed</span>}
          </li>
        );
      })}
    </ul>
  );
}

// Maintainer-only management panel: shows each overlay's current baseline ref vs
// the latest available SemVer tag, with a one-click "Promote to {latest}" bump
// and an explicit set-ref control (reusing RefCombobox so suggestions match the
// promote modal). Mirrors the promote modal's overlay-pins fetch/edit pattern,
// but as its own nav view since it writes the baseline rather than freezing a
// per-request pin. When the API reports the feature disabled, no table/controls
// are shown (mirrors how the promote modal hides its overlay block when disabled).
function OverlaysView({ canManageOverlays, targetId, onBack }: { canManageOverlays: boolean; targetId: string; onBack: () => void }) {
  const [enabled, setEnabled] = useState(true);
  const [overlays, setOverlays] = useState<OverlayBaselineRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [refData, setRefData] = useState<Record<string, { branches: string[]; tags: string[] }>>({});
  const [busyRepo, setBusyRepo] = useState<string | null>(null);
  const [status, setStatus] = useState<{ repo: string; ok: boolean; text: string } | null>(null);
  const requestedRefs = useRef<Set<string>>(new Set());

  const loadRefs = useCallback((repo: string) => {
    if (requestedRefs.current.has(repo)) return;
    requestedRefs.current.add(repo);
    api.overlayRefs(repo, targetId)
      .then(r => setRefData(prev => ({ ...prev, [repo]: { branches: r.branches, tags: r.tags } })))
      .catch(() => { requestedRefs.current.delete(repo); }); // let a later open retry
  }, [targetId]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.overlayBaseline(targetId);
      setEnabled(r.enabled);
      setOverlays(r.overlays);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [targetId]);

  useEffect(() => { refresh(); }, [refresh]);
  // Warm every repo's branch/tag list up front, same as the promote modal.
  useEffect(() => {
    if (overlays && overlays.length) overlays.forEach(o => loadRefs(o.repo));
  }, [overlays, loadRefs]);

  async function setRef(repo: string, ref: string) {
    const trimmed = ref.trim();
    if (!trimmed) return;
    setBusyRepo(repo);
    setStatus(null);
    try {
      const r = await api.setOverlayBaseline(repo, trimmed, targetId);
      setStatus({ repo, ok: true, text: `Set to ${trimmed} . commit ${r.commitSha.slice(0, 7)}` });
      setEdits(prev => { const next = { ...prev }; delete next[repo]; return next; });
      await refresh();
    } catch (e) {
      const msg = (e as Error).message;
      // The write endpoint's OverlayWriteConflict (409) message contains
      // "changed concurrently" — see console/src/github/client.ts.
      const conflict = /concurrently/i.test(msg);
      setStatus({ repo, ok: false, text: conflict ? 'Changed concurrently . refresh & retry.' : msg });
    } finally {
      setBusyRepo(null);
    }
  }

  return (
    <main className="overlays-view" data-testid="overlays-view">
      <button className="history-back" onClick={onBack} data-testid="overlays-back">&larr; back to promote</button>
      <h1 className="history-title">Overlay Baseline</h1>
      <p className="history-sub">The default ref each overlay resolves from when a new release request freezes its pins.</p>

      {!canManageOverlays ? (
        <p className="note">Read-only . not an overlay maintainer.</p>
      ) : loading ? (
        <Skeleton rows={3} />
      ) : loadError ? (
        <Empty>{loadError}</Empty>
      ) : !enabled ? (
        <Empty>Overlay baseline management is disabled.</Empty>
      ) : !overlays || overlays.length === 0 ? (
        <Empty>No overlays configured.</Empty>
      ) : (
        <div className="overlay-baseline-wrap">
          <table className="history-table" data-testid="overlays-baseline-table">
            <thead>
              <tr>
                <th>Repo</th>
                <th>Current ref</th>
                <th>Latest tag</th>
                <th></th>
                <th>Set ref</th>
              </tr>
            </thead>
            <tbody>
              {overlays.map(o => {
                const value = edits[o.repo] ?? o.ref;
                const busy = busyRepo === o.repo;
                const canBump = Boolean(o.latest) && o.latest !== o.ref;
                return (
                  <tr key={o.repo} data-testid={`overlay-baseline-row-${o.repo}`}>
                    <td className="overlay-repo">{o.repo}</td>
                    <td><code>{o.ref}</code></td>
                    <td>{o.latest ?? '—'}</td>
                    <td>
                      <button
                        className="btn btn-primary"
                        disabled={!canBump || busy}
                        onClick={() => o.latest && setRef(o.repo, o.latest)}
                        data-testid={`overlay-baseline-promote-${o.repo}`}
                      >
                        {busy ? 'Working…' : `Promote to ${o.latest ?? '—'}`}
                      </button>
                    </td>
                    <td className="overlay-set-ref">
                      <RefCombobox
                        value={value}
                        defaultRef={o.ref}
                        data={refData[o.repo]}
                        onOpen={() => loadRefs(o.repo)}
                        onChange={v => setEdits(prev => ({ ...prev, [o.repo]: v }))}
                        testId={`overlay-baseline-ref-${o.repo}`}
                      />
                      <button
                        className="btn btn-ghost"
                        disabled={busy || !value.trim() || value === o.ref}
                        onClick={() => setRef(o.repo, value)}
                        data-testid={`overlay-baseline-set-${o.repo}`}
                      >
                        {busy ? 'Setting…' : 'Set'}
                      </button>
                      {status && status.repo === o.repo && (
                        <div className={`note ${status.ok ? '' : 'modal-warn'}`} data-testid={`overlay-baseline-status-${o.repo}`}>
                          {status.text}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

type Theme = 'light' | 'dark';

function initialTheme(): Theme {
  try {
    const stored = localStorage.getItem('console-theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('console-theme', theme);
    } catch {
      /* ignore */
    }
  }, [theme]);
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="btn btn-ghost theme-toggle"
      onClick={() => setTheme(next)}
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
      data-testid="theme-toggle"
    >
      {theme === 'dark' ? (
        // sun
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
        </svg>
      ) : (
        // moon
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      )}
    </button>
  );
}

// A clickable commit SHA (opens the commit on GitHub). Used everywhere a SHA is
// shown - candidates, current prod, release requests, promote target, modal.
function CommitLink({ sha, className }: { sha: string; className?: string }) {
  const { repo } = useTargetUi();
  return (
    <a
      className={`sha-link ${className ?? ''}`}
      href={`https://github.com/${repo}/commit/${sha}`}
      target="_blank"
      rel="noreferrer"
      onClick={e => e.stopPropagation()}
    >
      <code>{sha7(sha)}</code>
    </a>
  );
}

// A link to the deploy run, styled like the gate links (icon + label).
function RunLink({ href, label = 'deploy run' }: { href: string; label?: string }) {
  return (
    <a className="run-link" href={href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
      <img className="run-link-bow" src="/icon-bow.png" width={18} height={18} alt="" aria-hidden />
      {label}
    </a>
  );
}

// The "prod cuts here" line, rendered immediately after the current-prod row.
// Rows above are newer (roll-forward), rows below are older (rollback).
function ProdCutDivider() {
  return (
    <li className="prod-cut" data-testid="prod-cut-divider" aria-hidden>
      <div className="prod-cut-rule"><span>prod cuts here</span></div>
      <div className="prod-cut-legend">
        <span className="rf">^ ahead of prod . roll-forward</span>
        <span className="rb">v behind prod . rollback</span>
      </div>
    </li>
  );
}

// Pages older commits in above the sentinel until the prod cut's row turns up.
// Stays mounted while a page loads (loadMore ignores the clicks) so focus holds.
function LoadMoreRow({ loading, exhausted, onLoad }: { loading: boolean; exhausted: boolean; onLoad: () => void }) {
  if (exhausted) {
    return (
      <li className="load-more">
        <span className="note">Load limit reached.</span>
      </li>
    );
  }
  return (
    <li className="load-more">
      <button
        type="button"
        className="btn btn-ghost btn-load-more"
        aria-disabled={loading}
        aria-busy={loading}
        onClick={onLoad}
        data-testid="load-more-btn"
      >
        Load more
      </button>
      {loading && <LoadingSpinner label="Loading more commits" />}
    </li>
  );
}

// Shown when the prod cut is too deep to fetch into the window. Summarizes the
// distance ("behind by X commits") instead of trying to render every row between.
function ProdSentinel({ sha, cutSha, count, aheadBy, beyondWindow, prodRowBelow }: { sha: string; cutSha: string; count: number; aheadBy?: number; beyondWindow?: boolean; prodRowBelow?: boolean }) {
  // Off-main prod (a deployments-kind target, e.g. polaris, before its first
  // console redeploy): the cut is drawn at the mirroring main commit, not prod's
  // own sha, so say so - the two are otherwise indistinguishable in this banner.
  const mirrorNote = cutSha !== sha ? <>, mirrored on <code>main</code> by <code>{sha7(cutSha)}</code></> : null;
  if (beyondWindow && typeof aheadBy === 'number') {
    return (
      <li className="prod-sentinel" data-testid="prod-sentinel">
        <span className="prod-sentinel-mark" aria-hidden>v</span>
        <span>
          {mirrorNote
            ? <>Current prod <code>{sha7(sha)}</code>{mirrorNote} is more than {aheadBy} commits behind main. The commits in between are not loaded.</>
            : `Current prod is more than ${aheadBy} commits behind main. The commits in between are not loaded.`}
        </span>
      </li>
    );
  }
  if (prodRowBelow && typeof aheadBy === 'number') {
    return (
      <li className="prod-sentinel" data-testid="prod-sentinel">
        <span className="prod-sentinel-mark" aria-hidden>v</span>
        <span>
          {mirrorNote
            ? <>Current prod <code>{sha7(sha)}</code>{mirrorNote} is {aheadBy} commits behind main. The commits in between are not loaded.</>
            : `Current prod is ${aheadBy} commits behind main. The commits in between are not loaded.`}
        </span>
      </li>
    );
  }
  return (
    <li className="prod-sentinel" data-testid="prod-sentinel">
      <span className="prod-sentinel-mark" aria-hidden>v</span>
      <span>
        Current prod <code>{sha7(sha)}</code>{mirrorNote} is{' '}
        {typeof aheadBy === 'number'
          ? `${aheadBy} commits behind main`
          : `older than these ${count} commits`}
        . everything shown is ahead of prod (roll-forward){typeof aheadBy === 'number' ? ', and more are not loaded' : ''}.
      </span>
    </li>
  );
}

function CandidateRow({
  c,
  selected,
  selectable,
  rollback,
  isCurrentProd,
  isRedeployTarget,
  promoState,
  onSelect,
}: {
  c: Candidate;
  selected: boolean;
  selectable: boolean;
  rollback: boolean;
  isCurrentProd: boolean;
  /** True for the candidate that mirrors current prod's code (deployments-kind
   * targets, e.g. polaris, whose off-main prod itself is usually not a
   * selectable main commit - see targets.ts's ProdSpec). Can coincide with
   * isCurrentProd once prod lands on main; both badges render in that case. */
  isRedeployTarget: boolean;
  /** Set when this commit is already shipping or already has a request open. */
  promoState?: RowPromoState;
  onSelect: () => void;
}) {
  const green = Boolean(c.staging && c.tests && c.e2e);
  return (
    <li className={`candidate ${c.policy ? 'has-policy' : ''} ${green ? '' : 'blocked'} ${selected ? 'selected' : ''} ${isCurrentProd ? 'current sticky-prod' : ''}`}>
      <label>
        <input
          type="radio"
          name="candidate"
          // Maintainers may select a non-green candidate too, but only the
          // break-glass Force path (extra-confirm) will promote it (P0-3).
          disabled={!selectable}
          checked={selected}
          onChange={onSelect}
          data-testid={`candidate-radio-${sha7(c.sha)}`}
        />
        <div className="candidate-body">
          <div className="candidate-head">
            <CommitLink sha={c.sha} />
            <span className="subject">{c.subject}</span>
            {typeof c.branchCommits === 'number' && (
              <span
                className="branch-chip"
                title={`This merge brought in ${c.branchCommits} branch commits. They never ran as main's tip, so they are not deployable on their own.`}
              >
                +{c.branchCommits} commits
              </span>
            )}
            {isCurrentProd && (
              <span className="badge badge-current" title="currently deployed to production" data-testid={`current-prod-${sha7(c.sha)}`}>
                current prod
              </span>
            )}
            {isRedeployTarget && (
              <span className="badge badge-redeploy" title="mirrors current production" data-testid={`redeploy-target-${sha7(c.sha)}`}>
                mirrors prod
              </span>
            )}
            {rollback && (
              <span className="badge badge-rollback" title="older than current production" data-testid={`rollback-${sha7(c.sha)}`}>
                rollback
              </span>
            )}
            {promoState === 'deploying' && (
              <span className="badge badge-enroute" title="a promote of this commit is deploying to production right now" data-testid={`deploying-${sha7(c.sha)}`}>
                deploying
              </span>
            )}
            {promoState === 'awaiting' && (
              <span className="badge badge-awaiting" title="a promote of this commit is already open, waiting on approvals" data-testid={`awaiting-${sha7(c.sha)}`}>
                requested
              </span>
            )}
          </div>
          <div className="candidate-foot">
            <span className="author">@{c.author}</span>
            <GateRow c={c} />
          </div>
        </div>
      </label>
    </li>
  );
}

// A gate reads its state as color + icon and links to the underlying GitHub
// status/check: success = green check, failure = red x, pending = amber dot
// (in-progress), cancelled = violet slash-circle (superseded by a newer run),
// skipped = grey dash (the gate never ran for this commit, e.g. a docs-only push),
// missing = grey dash (never reported). Only `success` is promotable (server-enforced).
// The state icon alone (no link/label), shared by the candidate gates and the
// How-to legend so both stay in sync.
function GateGlyph({ state }: { state: GateState }) {
  if (state === 'success') {
    return (
      <svg className="gate-check" viewBox="0 0 16 16" width="13" height="13" aria-hidden>
        <path d="M13.5 4.5 6.5 11.5 2.5 7.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (state === 'failure') {
    return (
      <svg className="gate-x" viewBox="0 0 16 16" width="13" height="13" aria-hidden>
        <path d="M4 4 12 12M12 4 4 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === 'cancelled') {
    return (
      <svg className="gate-slash" viewBox="0 0 16 16" width="13" height="13" aria-hidden>
        <circle cx="8" cy="8" r="5.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.3 4.3 11.7 11.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (state === 'pending') return <span className="gate-spin" aria-hidden />;
  return (
    <svg className="gate-dash" viewBox="0 0 16 16" width="13" height="13" aria-hidden>
      <path d="M3.5 8 12.5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Gate({ state, label, href }: { state: GateState; label: string; href: string }) {
  return (
    <a
      className={`gate state-${state}`}
      href={href}
      target="_blank"
      rel="noreferrer"
      title={`${label}: ${state} . open on GitHub`}
      onClick={e => e.stopPropagation()}
    >
      <GateGlyph state={state} />
      {label}
    </a>
  );
}

// The three gate chips for one candidate, worded per the selected target (see
// GATE_LABELS/gateLabelsFor): a `missing` state never renders as green, since
// `state-missing` styling is distinct from `state-success` in styles.css.
function GateRow({ c }: { c: Candidate }) {
  const { repo, gateLabels } = useTargetUi();
  const checksUrl = (sha: string) => `https://github.com/${repo}/commit/${sha}/checks`;
  if (c.policy && c.requiredChecks) return (
    <span className="checks">
      <Gate state={c.testsState ?? (c.tests ? 'success' : 'missing')} label={gateLabels.tests} href={c.testsUrl ?? checksUrl(c.sha)} />
      {c.requiredChecks.map(check => <Gate key={check.name} state={check.state} label={check.name} href={check.url ?? checksUrl(c.sha)} />)}
      <a className="badge" href={`https://github.com/${repo}/blob/${c.policy.sourceCommitSha}/${c.policy.path}`} target="_blank" rel="noreferrer">Policy {c.policy.sourceCommitSha.slice(0, 7)}</a>
    </span>
  );
  return (
    <span className="checks">
      <Gate state={c.testsState ?? (c.tests ? 'success' : 'missing')} label={gateLabels.tests} href={c.testsUrl ?? checksUrl(c.sha)} />
      <Gate state={c.stagingState ?? (c.staging ? 'success' : 'missing')} label={gateLabels.staging} href={c.stagingUrl ?? checksUrl(c.sha)} />
      {gateLabels.e2e && <Gate state={c.e2eState ?? (c.e2e ? 'success' : 'missing')} label={gateLabels.e2e} href={c.e2eUrl ?? checksUrl(c.sha)} />}
    </span>
  );
}

function RequestCard({
  r,
  canApprove,
  overlaysAllowed,
  busy,
  onApprove,
}: {
  r: ReleaseRequest;
  canApprove: boolean;
  overlaysAllowed: boolean;
  busy: boolean;
  onApprove: () => void;
}) {
  const pending = r.status === 'pending';
  const showApprove = pending && canApprove && r.required > 1;
  return (
    <li className={`request status-${r.status}`} data-testid={`request-${r.id}`}>
      <div className="request-head">
        <CommitLink sha={r.sha} />
        <span className={`badge status-${r.status}`}>{STATUS_LABEL[r.status]}</span>
        {r.forced && <span className="badge badge-forced" data-testid={`forced-${r.id}`}>FORCED</span>}
      </div>
      <div className="request-meta">
        <span>requested by @{r.requester}</span>
        {r.policy && <span>Policy {r.policy.sourceCommitSha.slice(0, 7)} . {r.policy.requiredChecks.length} required checks</span>}
        {r.createdAt && <span>{new Date(r.createdAt).toLocaleString()}</span>}
      </div>
      {/* Frozen at request creation - the tenant scope this exact request (and,
          once approved, this exact deploy) covers. Shown to every viewer, same as
          the sha/requester, since it's part of what's being approved. */}
      {r.tenants && r.tenants.length > 0 && (
        <div className="request-tenants" data-testid={`tenants-${r.id}`}>
          tenants: {r.tenants.join(', ')}
        </div>
      )}
      <div className="quorum">
        <div className="quorum-bar">
          {Array.from({ length: r.required }).map((_, i) => (
            <span key={i} className={`pip ${i < r.approvers.length ? 'filled' : ''}`} />
          ))}
        </div>
        <span className="quorum-label">
          {r.approvers.length} of {r.required} approvals
        </span>
      </div>
      {r.approvers.length > 0 && (
        <div className="approvers">{r.approvers.map(a => `@${a}`).join('  ')}</div>
      )}
      {/* Overlay pins expose the private premium-overlay roster (repo names + pinned
          SHAs/refs) — show them only to authorized maintainers on a target that has
          the feature, never in the non-maintainer read-only view and never for a
          target like polaris with no overlays at all. The backend (handlers.ts)
          enforces the same gate server-side, so this is defense in depth, not the
          only control. */}
      {canApprove && overlaysAllowed && r.overlayPins && Object.keys(r.overlayPins).length > 0 && (
        <details className="request-overlays" data-testid={`overlays-${r.id}`}>
          <summary>
            {Object.keys(r.overlayPins).length} overlay pins frozen
            {/* OVERRIDE = any non-main source ref. A baseline entry that legitimately floats on a non-main ref would also read as OVERRIDE — acceptable; revisit only if a baseline ref ever changes. */}
            {Object.values(r.overlayRefs ?? {}).some(ref => ref !== 'main') && (
              <span className="badge badge-override">OVERRIDE</span>
            )}
          </summary>
          <ul className="overlay-pin-list">
            {Object.entries(r.overlayPins).sort(([a], [b]) => a.localeCompare(b)).map(([repo, sha]) => (
              <li key={repo}>
                <span className="overlay-repo">{repo}</span>{' '}
                <a href={`https://github.com/Bike4Mind/${repo}/commit/${sha}`} target="_blank" rel="noreferrer">
                  <code>{sha.slice(0, 7)}</code>
                </a>{' '}
                <em>({r.overlayRefs?.[repo] ?? 'main'})</em>
              </li>
            ))}
          </ul>
        </details>
      )}
      {showApprove && (
        <button className="btn btn-approve" disabled={busy} onClick={onApprove} data-testid={`approve-btn-${r.id}`}>
          Approve
        </button>
      )}
      {r.runUrl && <RunLink href={r.runUrl} />}
    </li>
  );
}

// Always-visible promote control in the right column, so it needs no scrolling
// past a long candidate list. Mirrors the old bottom-of-list logic: a non-green
// selection can only go through the red break-glass Force path (P0-3).
function PromoteBox({
  me,
  canPromote,
  showForcePromote,
  redeployAllowed,
  canRedeploy,
  overlayPinsEnabled,
  selected,
  selectedGreen,
  rollback,
  isCurrentProd,
  isRedeployTarget,
  mirrorsProdSha,
  promoState,
  busy,
  onPromote,
  onForce,
}: {
  me: Me | null;
  canPromote: boolean;
  /** false for a target that declares no force-promote feature (e.g. polaris). */
  showForcePromote: boolean;
  /** false for a target without the redeploy feature: no redeploy at all. */
  redeployAllowed: boolean;
  /** true once the latest promotion is current prod and deployed green. */
  canRedeploy: boolean;
  /** false for a target that doesn't freeze overlay pins per release (e.g.
   * polaris): redeploy just re-ships the same commit, nothing to re-freeze. */
  overlayPinsEnabled: boolean;
  selected: Candidate | null;
  selectedGreen: boolean;
  rollback: boolean;
  isCurrentProd: boolean;
  /** True when `selected` is the candidate that mirrors current prod's code
   * (deployments-kind targets, e.g. polaris, whose off-main prod itself
   * usually isn't selectable - see isCurrentProd). Can coincide with
   * isCurrentProd once prod lands on main (the redeploy target then mirrors
   * itself); the ternary below checks isCurrentProd first, so that case
   * renders the isCurrentProd branch, not this one. */
  isRedeployTarget: boolean;
  /** The prod sha `selected` mirrors, for the "mirrors current prod X" note. */
  mirrorsProdSha?: string;
  /** Set when the selected commit is already shipping or already has a request open. */
  promoState?: RowPromoState;
  busy: boolean;
  onPromote: () => void;
  onForce: () => void;
}) {
  return (
    <div className="promote-box" data-testid="promote-box">
      <div className="panel-head">
        <h2>Promote</h2>
      </div>
      <div className="panel-body promote-box-body">
        {!me?.login ? (
          <a className="btn btn-primary promote-cta" href="/auth/login">
            Sign in with GitHub
          </a>
        ) : !canPromote ? (
          <p className="note">Read-only . not a deployer-maintainer.</p>
        ) : !selected ? (
          <p className="note" data-testid="promote-box-empty">Select a candidate from the list to promote.</p>
        ) : isCurrentProd && !redeployAllowed ? (
          <>
            <div className="promote-target">
              <CommitLink sha={selected.sha} />
              <span className="promote-subject">{selected.subject}</span>
            </div>
            <p className="promote-current-note" data-testid="promote-box-current">
              Already live in production . select a commit ahead of or behind the cut to promote.
            </p>
            <button className="btn btn-primary promote-cta" disabled data-testid="promote-open-btn">
              Promote to production
            </button>
          </>
        ) : isCurrentProd ? (
          <>
            <div className="promote-target">
              <CommitLink sha={selected.sha} />
              <span className="promote-subject">{selected.subject}</span>
            </div>
            <p className="promote-current-note" data-testid="promote-box-current">
              {overlayPinsEnabled
                ? 'Live in production. Redeploy ships this same commit with overlay pins re-frozen from the Overlay baseline.'
                : 'Live in production. Redeploy re-ships this same commit.'}
            </p>
            {!canRedeploy ? (
              <>
                <p className="note" data-testid="promote-box-redeploy-blocked">
                  Redeploy unlocks once the latest promotion has deployed green.
                </p>
                <button className="btn btn-primary promote-cta" disabled data-testid="redeploy-open-btn">
                  Redeploy to production
                </button>
              </>
            ) : !selectedGreen && showForcePromote ? (
              <button
                className="btn btn-danger promote-cta"
                disabled={busy}
                onClick={onForce}
                data-testid="force-redeploy-open-btn"
              >
                Force redeploy (bypass gate)
              </button>
            ) : !selectedGreen ? (
              <>
                <p className="note" data-testid="promote-box-gated">All gates must pass to promote this target.</p>
                <button className="btn btn-primary promote-cta" disabled data-testid="redeploy-open-btn">
                  Redeploy to production
                </button>
              </>
            ) : (
              <button
                className="btn btn-primary promote-cta"
                disabled={busy}
                onClick={onPromote}
                data-testid="redeploy-open-btn"
              >
                Redeploy to production
              </button>
            )}
          </>
        ) : isRedeployTarget && redeployAllowed ? (
          <>
            <div className="promote-target">
              <CommitLink sha={selected.sha} />
              <span className="promote-subject">{selected.subject}</span>
            </div>
            <p className="promote-current-note" data-testid="promote-box-mirrors-prod">
              Mirrors current prod <code>{mirrorsProdSha ? sha7(mirrorsProdSha) : ''}</code> . ships the identical code via its commit on <code>main</code>.
            </p>
            {promoState && (
              <p className="promote-inflight-note" data-state={promoState} data-testid="promote-box-inflight">
                {promoState === 'deploying'
                  ? 'Already deploying to production . promoting again dispatches a second run.'
                  : 'A request for this commit is already open . promoting again creates a second one.'}
              </p>
            )}
            {!selectedGreen && showForcePromote ? (
              <button
                className="btn btn-danger promote-cta"
                disabled={busy}
                onClick={onForce}
                data-testid="force-redeploy-open-btn"
              >
                Force redeploy (bypass gate)
              </button>
            ) : !selectedGreen ? (
              <>
                <p className="note" data-testid="promote-box-gated">All gates must pass to promote this target.</p>
                <button className="btn btn-primary promote-cta" disabled data-testid="redeploy-open-btn">
                  Redeploy to production
                </button>
              </>
            ) : (
              <button
                className="btn btn-primary promote-cta"
                disabled={busy}
                onClick={onPromote}
                data-testid="redeploy-open-btn"
              >
                Redeploy to production
              </button>
            )}
          </>
        ) : (
          <>
            <div className="promote-target">
              <CommitLink sha={selected.sha} />
              <span className="promote-subject">{selected.subject}</span>
            </div>
            {rollback && (
              <p className="promote-rollback-note" data-testid="promote-box-rollback">
                Older than current prod . this is a rollback.
              </p>
            )}
            {/* Warn, don't block: re-promoting a wedged deploy is legitimate, and
                nothing on the server rejects a duplicate request for the same SHA. */}
            {promoState && (
              <p className="promote-inflight-note" data-state={promoState} data-testid="promote-box-inflight">
                {promoState === 'deploying'
                  ? 'Already deploying to production . promoting again dispatches a second run.'
                  : 'A request for this commit is already open . promoting again creates a second one.'}
              </p>
            )}
            {!selectedGreen && showForcePromote ? (
              <button
                className="btn btn-danger promote-cta"
                disabled={busy}
                onClick={onForce}
                data-testid="force-promote-open-btn"
              >
                Force promote (bypass gate)
              </button>
            ) : !selectedGreen ? (
              <>
                <p className="note" data-testid="promote-box-gated">All gates must pass to promote this target.</p>
                <button className="btn btn-primary promote-cta" disabled data-testid="promote-open-btn">
                  Promote to production
                </button>
              </>
            ) : (
              <button
                className="btn btn-primary promote-cta"
                disabled={busy}
                onClick={onPromote}
                data-testid="promote-open-btn"
              >
                Promote to production
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// A free-text ref field with a click-to-open, filter-as-you-type dropdown of the
// overlay repo's branches + tags (grouped). Visible caret affordance; a few rows
// show at once and the rest scroll. Typing narrows both groups (so tags are
// searchable), and any value the operator types still stands — the list is only a
// picker, an explicit SHA or unlisted ref works exactly as before.
function RefCombobox({ value, defaultRef, data, onOpen, onChange, testId }: {
  value: string;
  defaultRef: string;
  data?: { branches: string[]; tags: string[] };
  onOpen: () => void;
  onChange: (v: string) => void;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = value.trim().toLowerCase();
  const filt = (arr: string[]) => arr.filter(s => !q || s.toLowerCase().includes(q));
  const branches = data ? filt(data.branches) : [];
  const tags = data ? filt(data.tags) : [];
  const items = [
    ...branches.map(r => ({ kind: 'branch' as const, ref: r })),
    ...tags.map(r => ({ kind: 'tag' as const, ref: r })),
  ];

  const openList = () => { onOpen(); setOpen(true); };
  const choose = (ref: string) => { onChange(ref); setOpen(false); setActive(-1); };

  return (
    <div className="ref-combo" ref={wrapRef}>
      <div className="ref-combo-field">
        <input
          value={value}
          spellCheck={false}
          autoComplete="off"
          onFocus={openList}
          onChange={e => { onChange(e.target.value); setOpen(true); setActive(-1); }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openList(); setActive(a => Math.min(a + 1, items.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
            else if (e.key === 'Enter' && open && active >= 0 && items[active]) { e.preventDefault(); choose(items[active].ref); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          data-testid={testId}
        />
        <button
          type="button"
          className="ref-combo-caret"
          tabIndex={-1}
          aria-label="show branches and tags"
          onMouseDown={e => { e.preventDefault(); if (open) setOpen(false); else openList(); }}
        >▾</button>
      </div>
      {open && (
        <ul className="ref-combo-list" role="listbox">
          {!data && <li className="ref-combo-note">loading refs…</li>}
          {data && items.length === 0 && <li className="ref-combo-note">no match — free-text / SHA still works</li>}
          {items.map((it, i) => (
            <Fragment key={it.kind + ':' + it.ref}>
              {(i === 0 || items[i - 1].kind !== it.kind) && (
                <li className="ref-combo-group">{it.kind === 'branch' ? `branches (${branches.length})` : `tags (${tags.length})`}</li>
              )}
              <li
                role="option"
                aria-selected={i === active}
                className={`ref-combo-opt${i === active ? ' active' : ''}`}
                onMouseDown={e => { e.preventDefault(); choose(it.ref); }}
                onMouseEnter={() => setActive(i)}
              >
                <span className={`ref-combo-kind ${it.kind}`}>{it.kind === 'tag' ? 'tag' : 'br'}</span>
                <span className="ref-combo-name">{it.ref}</span>
                {it.ref === defaultRef && <span className="ref-combo-def">default</span>}
              </li>
            </Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConfirmModal({
  c,
  force,
  rollback,
  redeploy,
  mirrorsProdSha,
  prodPins,
  busy,
  targetId,
  tenants,
  overlaysAllowed,
  onCancel,
  onConfirm,
}: {
  c: Candidate;
  force: boolean;
  rollback: boolean;
  /** True when `c` is already current prod (bike4mind) or is the redeploy target
   * that mirrors it (a deployments-kind target, e.g. polaris) - either way this
   * request re-ships code already live. */
  redeploy?: boolean;
  /** Set only for the redeploy-target case above: the prod sha `c` mirrors, so
   * the dialog can say what it re-ships instead of bike4mind's overlay wording. */
  mirrorsProdSha?: string;
  /** The prod request's frozen overlay pins, so the overlays table can mark which moved. */
  prodPins?: Record<string, string>;
  busy: boolean;
  targetId: string;
  /** The target's configured tenant scope (e.g. polaris's ['all','polaris','feq']),
   * already restricted to the redeploy-safe subset when `mirrorsProdSha` is set
   * (see redeployableTenants). Absent means the target doesn't scope by tenant. */
  tenants?: string[];
  /** false for a target without the overlayPins feature - the API 404s those routes, so this skips the fetch entirely. */
  overlaysAllowed: boolean;
  onCancel: () => void;
  onConfirm: (overlayRefs?: Record<string, string>, tenants?: string[]) => void;
}) {
  // Break-glass requires an explicit acknowledgement before Confirm enables.
  const [ack, setAck] = useState(false);
  // Defaults to 'all' when it's actually offered, else the first tenant that is
  // (a redeploy can restrict the list - see redeployableTenants - and defaulting
  // to an option that isn't rendered would silently submit it anyway).
  const [tenant, setTenant] = useState(() => (tenants && tenants.length > 0 && !tenants.includes('all')) ? tenants[0] : 'all');

  // The overlay set this request will FREEZE: baseline refs resolved to HEAD
  // SHAs right now, each row overridable (alt branch or explicit SHA) before
  // the request is created. Approvers approve exactly the frozen result.
  const [overlays, setOverlays] = useState<OverlayPinRow[] | null>(null);
  const [overlaysEnabled, setOverlaysEnabled] = useState(false);
  const [overlayErr, setOverlayErr] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Branch/tag suggestions per overlay repo for the ref combobox. The input stays
  // free-text (an explicit SHA or any unlisted ref still works); this only feeds
  // the dropdown. Prefetched for every repo as soon as the overlay set resolves,
  // so the dropdown is populated the instant a field is clicked.
  const [refData, setRefData] = useState<Record<string, { branches: string[]; tags: string[] }>>({});
  const requestedRefs = useRef<Set<string>>(new Set());
  const loadRefs = useCallback((repo: string) => {
    if (requestedRefs.current.has(repo)) return;
    requestedRefs.current.add(repo);
    api.overlayRefs(repo, targetId)
      .then(r => setRefData(prev => ({ ...prev, [repo]: { branches: r.branches, tags: r.tags } })))
      .catch(() => { requestedRefs.current.delete(repo); }); // let a later open retry
  }, [targetId]);
  useEffect(() => {
    // A target without the overlayPins feature 404s these routes server-side;
    // skip the call entirely so the block simply doesn't appear (no error toast).
    if (!overlaysAllowed) { setOverlays([]); return; }
    let cancelled = false;
    api.overlayPins(targetId)
      .then(r => { if (!cancelled) { setOverlaysEnabled(r.enabled); setOverlays(r.pins); } })
      .catch(e => { if (!cancelled) { setOverlays([]); setOverlayErr((e as Error).message); } });
    return () => { cancelled = true; };
  }, [overlaysAllowed, targetId]);
  // Warm every repo's branch/tag list up front (parallel), so opening any row's
  // combobox is instant instead of paying a per-field fetch on first focus.
  useEffect(() => {
    if (overlays && overlays.length) overlays.forEach(o => loadRefs(o.repo));
  }, [overlays, loadRefs]);
  // Only rows the operator actually changed are sent as overrides.
  const overrides = Object.keys(edits).length ? edits : undefined;
  // `tenant`'s default is a lazy useState initializer - it only runs once, on
  // mount - so if `tenants` narrows while this dialog is still open (a poll
  // refetches the target, or a redeploy's tenant scope changes), the selected
  // value can go stale. Re-check against the current list at submit time
  // rather than trusting the initial pick.
  const tenantStillOffered = !tenants || tenants.length === 0 || tenants.includes(tenant);
  // Hold Confirm until the overlay fetch settles, so nobody promotes without
  // having seen the default SHAs (or whether any were edited). A failed fetch
  // settles too: the server re-resolves the set at create anyway, and the
  // error warning below stays visible.
  const overlaysPending = overlaysAllowed && overlays === null;

  return (
    <div className="modal-scrim" onClick={onCancel}>
      <div className={`modal ${force ? 'modal-danger' : ''}`} onClick={e => e.stopPropagation()} role="dialog" aria-modal>
        <h2>{force ? (redeploy ? 'Force redeploy (break-glass)' : 'Force promote (break-glass)') : redeploy ? 'Redeploy to production' : 'Promote to production'}</h2>
        {force ? (
          <p className="modal-sub modal-warn" data-testid="force-warning">
            This bypasses the tests + staging gate and ships an unvalidated commit to production. Use only when the
            gate is wrong (e.g. staging is down), not to skip a real failure. The bypass is logged loudly (Slack +
            the deploy run) and recorded on the request.
          </p>
        ) : redeploy && mirrorsProdSha ? (
          <p className="modal-sub" data-testid="modal-mirrors-prod">
            This re-ships the same code as prod <code>{sha7(mirrorsProdSha)}</code>, via its equivalent commit on <code>main</code>.
          </p>
        ) : redeploy ? (
          <p className="modal-sub">
            This redeploys the commit already in production. Overlay pins are re-frozen now, so any overlay whose ref moved ships its new SHA.
          </p>
        ) : (
          <p className="modal-sub">This dispatches a production deploy. The workflow re-verifies tests + staging before shipping.</p>
        )}
        <div className="modal-sha">
          <CommitLink sha={c.sha} />
          <span>{c.subject}</span>
        </div>
        {rollback && (
          <div className="modal-rollback" data-testid="rollback-warning">
            <strong>This is a rollback.</strong> This commit is older than what is currently in production. Deploying
            it reverts <em>code</em> only . data migrations applied by newer releases are <strong>NOT</strong> reverted,
            so a release with a destructive migration will not cleanly undo. Confirm the target release was
            backward-compatible (see the expand/contract policy in RUNBOOK.md), or prefer a roll-forward fix.
          </div>
        )}
        {overlaysEnabled && (
          <div className="modal-overlays" data-testid="overlay-pins">
            <div className="overlays-head">
              <strong>Premium overlays</strong>
              <span className="overlays-sub">frozen into this release at creation . edit a ref to ship a branch/SHA</span>
            </div>
            {overlays === null && <p className="note">Resolving overlay refs…</p>}
            {overlays && overlays.length > 0 && (
              <table className="overlays-table">
                <tbody>
                  {overlays.map(o => {
                    const edited = edits[o.repo] !== undefined;
                    return (
                      <tr key={o.repo} className={edited ? 'overlay-edited' : ''}>
                        <td className="overlay-repo">
                          {o.repo}
                          {/* Under the name, not beside the SHA: the SHA column is nowrap and would push the table past the modal. */}
                          {redeploy && !edited && prodPins?.[o.repo] && prodPins[o.repo] !== o.sha && (
                            <span className="overlay-was" data-testid={`overlay-was-${o.repo}`}>was {prodPins[o.repo].slice(0, 7)}</span>
                          )}
                        </td>
                        <td className="overlay-ref">
                          <RefCombobox
                            value={edits[o.repo] ?? o.ref}
                            defaultRef={o.ref}
                            data={refData[o.repo]}
                            onOpen={() => loadRefs(o.repo)}
                            onChange={(v) => setEdits(prev => {
                              const next = { ...prev };
                              if (v === o.ref) delete next[o.repo]; else next[o.repo] = v;
                              return next;
                            })}
                            testId={`overlay-ref-${o.repo}`}
                          />
                        </td>
                        <td className="overlay-sha">
                          {edited ? <em>resolves at create</em> : <code title={o.sha}>{o.sha.slice(0, 7)}</code>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
        {overlayErr && (
          <p className="modal-sub modal-warn" data-testid="overlay-pins-error">
            Could not resolve overlay pins: {overlayErr}. A normal promote will be blocked; Force promote ships the
            baseline without a frozen set.
          </p>
        )}
        {tenants && tenants.length > 0 && (
          <div className="modal-tenants" role="radiogroup" aria-label="Tenant scope" data-testid="tenant-radio">
            <strong>Tenant scope</strong>
            {tenants.map(t => (
              <label key={t} className="modal-tenant-opt">
                <input
                  type="radio"
                  name="tenant"
                  value={t}
                  checked={tenant === t}
                  onChange={() => setTenant(t)}
                  data-testid={`tenant-opt-${t}`}
                />
                {t}
              </label>
            ))}
          </div>
        )}
        {tenants && tenants.length > 0 && !tenantStillOffered && (
          <p className="modal-sub modal-warn" data-testid="tenant-stale-warning">
            The offered tenants changed since this dialog opened. Pick one again before confirming.
          </p>
        )}
        {force && (
          <label className="modal-ack">
            <input
              type="checkbox"
              checked={ack}
              onChange={e => setAck(e.target.checked)}
              data-testid="force-ack"
            />
            I understand this bypasses the tests + staging gate.
          </label>
        )}
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={`btn ${force ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => onConfirm(overrides, tenants && tenants.length > 0 ? [tenant] : undefined)}
            disabled={busy || (force && !ack) || !tenantStillOffered || overlaysPending}
            data-testid="promote-confirm-btn"
          >
            {busy ? 'Dispatching...' : force ? (redeploy ? 'Force redeploy' : 'Force promote') : redeploy ? 'Confirm redeploy' : 'Confirm promote'}
          </button>
        </div>
        {overlaysPending && (
          <p className="note" data-testid="overlay-pins-loading">Loading overlay pins…</p>
        )}
      </div>
    </div>
  );
}

const TEAM_URL = 'https://github.com/orgs/Bike4Mind/teams/deployer-maintainers';
const FUTURUM_TEAM_URL = 'https://github.com/orgs/FuturumGroup/teams/deployer-maintainers';

// Shown to a signed-out visitor sent back by a refused sign-in (see clearSigninDenied).
function SigninDeniedNotice() {
  return (
    <div className="signin-denied" role="status" aria-live="polite" data-testid="signin-denied">
      <span className="signin-denied-mark" aria-hidden>!</span>
      <div className="signin-denied-body">
        <p className="signin-denied-head">You're not on a promoter team</p>
        <p>
          GitHub signed you in, but your account is not on an authorized promoter team, so no session was started.
          Ask a console administrator which team grants access to your app, then sign in again.
        </p>
        <p>
          The "Request" button GitHub shows next to your organizations on the sign-in screen asks your org to
          approve this app. It doesn't grant console access, so you don't need to click it.
        </p>
      </div>
    </div>
  );
}

// Legend for the How-to tab; reuses the exact gate icons/colors from the board.
function GateLegend() {
  const rows: { state: GateState; word: string; meaning: string }[] = [
    { state: 'success', word: 'passed', meaning: 'the gate is green (promotable)' },
    { state: 'failure', word: 'failed', meaning: 'this check failed' },
    { state: 'pending', word: 'running', meaning: 'still in progress' },
    { state: 'cancelled', word: 'cancelled', meaning: 'superseded by a newer run' },
    { state: 'skipped', word: 'skipped', meaning: 'the gate never ran for this commit (e.g. a docs-only change)' },
    { state: 'missing', word: 'not run', meaning: 'no qualifying evidence reported for this commit' },
  ];
  return (
    <ul className="gate-legend">
      {rows.map(r => (
        <li key={r.state}>
          <span className={`gate state-${r.state}`}><GateGlyph state={r.state} /> {r.word}</span>
          <span className="gl-meaning">{r.meaning}</span>
        </li>
      ))}
    </ul>
  );
}

function HowTo({ maintainers, me, targetId }: { maintainers: Maintainer[]; me: Me | null; targetId: string }) {
  const teams: Record<string, string> = {
    bike4mind: 'Bike4Mind/deployer-maintainers',
    polaris: 'FuturumGroup/deployer-maintainers',
    bedrocknews: 'MillionOnMars/starlight',
    starlight: 'MillionOnMars/starlight',
    k2kanji: 'MillionOnMars/k2kanji-deployers',
    'deployer-sandbox': 'MillionOnMars/deployer-sandbox-maintainers',
    'paint-box': 'MillionOnMars/paintbox-deployers',
    fleet: 'MillionOnMars/fleet-deployers',
    q: 'MillionOnMars/q-deployers',
    'b4m-web': 'MillionOnMars/b4m-web-deployers',
  };
  const team = teams[targetId] ?? teams.bike4mind;
  const teamUrl = `https://github.com/orgs/${team.replace('/', '/teams/')}`;
  return (
    <main className="howto">
      <section className="doc">
        <h2>What this is</h2>
        <p>
          Deploy Console promotes an approved <strong>exact commit SHA from main</strong> to production.
          It supports Bike4Mind, Polaris, Bedrocknews, K2Kanji, Starlight, PaintBox, Fleet and Bike4Mind Web, plus the isolated Deployer Sandbox test target. Each has its own
          checks, environments and authorized team. Product logos identify the app; the cargo mark identifies the console. Select the app before choosing a release.
          The console dispatches that app's configured production workflow; existing CLI and branch-based
          release paths remain available. A console release does not retire those paths.
        </p>

        <h2>What a promotion actually deploys</h2>
        <p>
          A promotion ships a <strong>snapshot of the whole repo at one commit</strong> - that commit and all its
          history (everything older), never the commits newer than it. It is not a range and not a cherry-pick.
          Picking the 3rd candidate from the top deploys the repo exactly as it stood at that commit: the two newer
          commits above it are excluded, everything below (older) is already part of it. You usually pick the newest
          commit that is fully green. Newer ones above it may not have passed the staging gate yet.
        </p>

        <h2>Reading the board</h2>
        <p>
          Candidates come from the selected app's main history. Bike4Mind's mainline view shows first-parent
          commits and groups merged branch work under a <strong>+N commits</strong> chip. Other apps and the
          fallback history view can include branch commits. The list scrolls inside a fixed frame with current prod pinned to the bottom edge,
          so the <strong>prod cuts here</strong> divider stays reachable without losing the newest candidates off
          the top. The candidate whose row is highlighted with a <strong>CURRENT PROD</strong> chip is what is live
          right now. Rows <em>above</em> the divider are newer (a roll-forward); rows <em>below</em> are older (a
          rollback, badged as such). If prod has dropped further behind than the frame holds, a note below the list
          takes its place and says how many commits behind it is.
        </p>
        <p>
          A row can also carry a <strong>deploying</strong> chip (a promote of that exact commit is shipping right
          now) or a <strong>requested</strong> chip (a request for it is open but has not reached quorum).
          Both mean someone already promoted that commit - promoting it again dispatches a second run rather than
          joining the first, so check the chip before you click.
        </p>

        <h2>What the gate badges mean</h2>
        <p>Gate badges follow the selected app's configuration and link to the evidence on GitHub. Every required gate must pass on the exact candidate SHA. Missing, skipped or failing evidence blocks a normal promotion.</p>
        <GateLegend />
        <p className="howto-note">
          Bike4Mind requires tests, staging and <code>e2e/core</code>. Polaris uses <code>CI Gate</code>
          and staging deployments for the selected tenants. Bedrocknews, K2Kanji and Deployer Sandbox require
          <code>deploy / Validate</code>, <code>deploy / Deploy staging</code> and their configured staging deployment.
          Their shared production adapter independently checks the approved bot identity, main ancestry,
          required checks and staging evidence before checking out the requested SHA.
        </p>

        <h2>Who can promote</h2>
        <p>
          Members of the selected app's authorized team:{' '}
          <a href={TEAM_URL} target="_blank" rel="noreferrer">Bike4Mind/deployer-maintainers</a> for bike4mind,{' '}
          <a href={FUTURUM_TEAM_URL} target="_blank" rel="noreferrer">FuturumGroup/deployer-maintainers</a> for
          Polaris. Bedrocknews and Starlight share MillionOnMars/starlight. K2Kanji, PaintBox, Fleet, Bike4Mind Web, Q and Deployer Sandbox each use their dedicated team. The selected app uses <a href={teamUrl} target="_blank" rel="noreferrer">{team}</a>. Signing in with GitHub only starts a session for a member of an enabled target's team, or of
          Bike4Mind/premium-overlay-maintainers (which manages the overlay baseline but cannot promote). Anyone
          else is sent back here with a notice and no session. A session without promote rights on the selected
          target is read-only: candidates and history are visible, the promote/approve controls are hidden, and
          the API rejects them.
        </p>

        <h2>Promote a release</h2>
        <ol>
          <li>Sign in with GitHub. A green MAINTAINER badge means you can promote.</li>
          <li>Select the app, then pick a candidate with every required gate green. It appears in the <strong>Promote</strong> box on the right.</li>
          <li>Click <strong>Promote to production</strong> and confirm. The workflow re-verifies the gate before it ships (fail-closed).</li>
          <li>At quorum 1 it dispatches immediately; at higher quorum it waits for other maintainers to Approve. The request card flips to DISPATCHED, with a <em>deploy run</em> link to watch it. Dispatch is not deployment success; check the run and production evidence.</li>
        </ol>

        <h2>Rolling back</h2>
        <p>
          Promote an older commit (one below the cut line). The console flags it as a <strong>rollback</strong> and the
          confirm dialog warns you: it reverts <strong>code only</strong> - data migrations from newer releases do
          <strong> not</strong> roll back. Confirm the target was backward-compatible (see the expand/contract policy in
          the runbook) or prefer a roll-forward fix.
        </p>

        <h2>Break-glass (staging is down)</h2>
        <p>
          Bike4Mind supports <strong>Force promote</strong> - a maintainer-only, deliberately red
          path that bypasses the gate. It needs an extra confirmation, posts loudly to Slack, and records the bypass on
          the release. Use it when the gate is wrong (e.g. staging is down), not to skip a real failure.
          Polaris, Bedrocknews, K2Kanji and Deployer Sandbox do not offer this bypass.
        </p>

        <h2>Add a maintainer</h2>
        <p>
          Add them to the selected app's <a href={teamUrl} target="_blank" rel="noreferrer">{team}</a> team (org owner,
          on GitHub). A newly added person must sign out and back in to flip from READ-ONLY to MAINTAINER. Note that
          membership is also <strong>re-checked live at promote time</strong>, so a removed maintainer is blocked
          immediately, even within an active session.
        </p>
      </section>

      <aside className="maintainers-card">
        <div className="panel-head">
          <h2>Maintainers</h2>
          <a className="panel-hint" href={teamUrl} target="_blank" rel="noreferrer">manage on GitHub</a>
        </div>
        <div className="panel-body">
          {!me?.login ? (
            <p className="note">Sign in to see the current roster.</p>
          ) : maintainers.length === 0 ? (
            <p className="note">
              Roster unavailable - the GitHub App needs "Organization members: Read". Manage the team{' '}
              <a href={teamUrl} target="_blank" rel="noreferrer">here</a>.
            </p>
          ) : (
            <ul className="maintainers">
              {maintainers.map(m => (
                <li key={m.login}>
                  <a href={m.htmlUrl} target="_blank" rel="noreferrer">
                    {m.avatarUrl && <img src={m.avatarUrl} alt="" width={22} height={22} />}
                    <span>@{m.login}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </main>
  );
}

function Panel({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="panel">
      <div className="panel-head">
        <h2>{title}</h2>
        <div className="panel-head-right">
          {hint && <span className="panel-hint">{hint}</span>}
          {action}
        </div>
      </div>
      <div className="panel-body">{children}</div>
    </div>
  );
}

function RefreshButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  return (
    <button
      className="btn btn-ghost btn-refresh"
      onClick={onClick}
      disabled={busy}
      title="Refresh candidates + requests"
      aria-label="Refresh"
      data-testid="refresh-btn"
    >
      <svg className={busy ? 'spin' : ''} viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
      </svg>
      {busy ? 'Refreshing' : 'Refresh'}
    </button>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>;
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton-row" />
      ))}
    </div>
  );
}
